/**
 * Phase 3: Table Definitions
 * AI-assisted table definitions, one at a time
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Table, Loader2, Check, Server, Database, ChevronRight, 
  SkipForward, Play, CheckCircle2, Wand2, RefreshCw, Ban, Eye, EyeOff, AlertTriangle
} from 'lucide-react';
import { getNextTableForDefinition, saveTableDefinition, aiSuggestTableDefinition, discoverRelationships, skipTable, getSkippedTables, unskipTable, ignoreTable, getIgnoredTables, unignoreTable } from '../../services/api';

const Phase3TableDefinitions = ({ onComplete, isAdmin, onRelationshipsDiscovered }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [state, setState] = useState({
    currentTable: null,
    progress: { defined: 0, total: 0, percentage: 0, skipped: 0, ignored: 0 },
    remaining: 0,
    loading: false,
    saving: false,
    definition: '',
    allDone: false,
    aiSuggesting: false,
    aiSuggestion: '',
    discoveringRelationships: false,
    relationshipsDiscovered: false,
    skippedTables: [],
    ignoredTables: [],
    skippedLoading: false,
    showIgnoreDialog: false,
    ignoreReason: ''
  });

  const loadTablesAndFetchNext = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, skippedLoading: true }));
    try {
      // Load skipped and ignored tables from backend
      const [skippedResult, ignoredResult] = await Promise.all([
        getSkippedTables(),
        getIgnoredTables()
      ]);
      
      const skippedList = skippedResult.skipped_tables || [];
      const ignoredList = ignoredResult.ignored_tables || [];
      
      setState(prev => ({
        ...prev,
        skippedTables: skippedList,
        ignoredTables: ignoredList,
        skippedLoading: false
      }));
      
      // Then fetch next table
      await fetchNextInternal(skippedList, ignoredList);
    } catch (error) {
      console.error('Failed to load tables:', error);
      setState(prev => ({ ...prev, loading: false, skippedLoading: false }));
      await fetchNextInternal([], []);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadTablesAndFetchNext();
  }, [loadTablesAndFetchNext]);

  const fetchNextInternal = async (currentSkippedList, currentIgnoredList) => {
    setState(prev => ({ ...prev, loading: true, saving: false }));
    try {
      const result = await getNextTableForDefinition();
      if (result.table) {
        setState(prev => ({
          ...prev,
          currentTable: result.table,
          progress: result.progress,
          remaining: result.remaining,
          definition: '',
          aiSuggestion: '',
          allDone: false,
          loading: false,
          saving: false,
          skippedTables: currentSkippedList || prev.skippedTables,
          ignoredTables: currentIgnoredList || prev.ignoredTables
        }));
        onCompleteRef.current(result.progress.percentage);
      } else {
        setState(prev => ({
          ...prev,
          currentTable: null,
          progress: result.progress,
          allDone: true,
          loading: false,
          saving: false,
          skippedTables: currentSkippedList || prev.skippedTables,
          ignoredTables: currentIgnoredList || prev.ignoredTables
        }));
        onCompleteRef.current(result.progress.percentage);
      }
    } catch (error) {
      console.error('Failed to fetch next table:', error);
      toast.error('Failed to load next table');
      setState(prev => ({ ...prev, loading: false, saving: false }));
    }
  };

  const fetchNext = async () => {
    await fetchNextInternal(state.skippedTables, state.ignoredTables);
  };

  const handleAISuggest = async () => {
    if (!state.currentTable) return;
    
    setState(prev => ({ ...prev, aiSuggesting: true }));
    
    try {
      const { server, database, table, columns, row_count } = state.currentTable;
      const result = await aiSuggestTableDefinition(server, database, table, columns, row_count || 0);
      
      if (result.success && result.suggestion) {
        setState(prev => ({ 
          ...prev, 
          aiSuggestion: result.suggestion,
          definition: result.suggestion,
          aiSuggesting: false 
        }));
        
        if (result.fallback) {
          toast.info('AI suggestion generated (using fallback)');
        } else {
          toast.success('AI suggestion generated - please review and edit');
        }
      } else {
        throw new Error('No suggestion returned');
      }
    } catch (error) {
      console.error('AI suggestion error:', error);
      toast.error('Failed to generate AI suggestion');
      setState(prev => ({ ...prev, aiSuggesting: false }));
    }
  };

  const handleSave = async () => {
    if (!state.currentTable || !state.definition.trim()) {
      toast.error('Please enter a definition');
      return;
    }
    
    setState(prev => ({ ...prev, saving: true }));
    try {
      const { server, database, table } = state.currentTable;
      await saveTableDefinition(server, database, table, state.definition);
      toast.success(`Definition saved for ${table}`);
      await fetchNext();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save definition');
      setState(prev => ({ ...prev, saving: false }));
    }
  };

  const handleSkip = async () => {
    if (!state.currentTable) return;
    
    const { server, database, table, columns, row_count } = state.currentTable;
    
    setState(prev => ({ ...prev, saving: true }));
    
    try {
      // Persist skip to backend
      await skipTable(server, database, table);
      
      // Update local state with the skipped table info
      const newSkipped = [...state.skippedTables, { server, database, table, columns, row_count }];
      
      toast.info(`Skipped: ${table}`, { 
        description: 'You can define it later from the skipped list',
        duration: 2000 
      });
      
      // Fetch next table with updated skipped list
      await fetchNextInternal(newSkipped, state.ignoredTables);
    } catch (error) {
      console.error('Failed to skip table:', error);
      toast.error('Failed to skip table');
      setState(prev => ({ ...prev, saving: false }));
    }
  };

  const handleIgnore = async () => {
    if (!state.currentTable) return;
    
    const { server, database, table } = state.currentTable;
    const reason = state.ignoreReason.trim() || 'Not needed for analysis';
    
    setState(prev => ({ ...prev, saving: true, showIgnoreDialog: false }));
    
    try {
      // Persist ignore to backend
      await ignoreTable(server, database, table, reason);
      
      // Update local state
      const newIgnored = [...state.ignoredTables, { server, database, table, reason }];
      
      toast.success(`Ignored: ${table}`, { 
        description: 'AI will skip this table in queries',
        duration: 3000 
      });
      
      // Fetch next table
      await fetchNextInternal(state.skippedTables, newIgnored);
      setState(prev => ({ ...prev, ignoreReason: '' }));
    } catch (error) {
      console.error('Failed to ignore table:', error);
      toast.error('Failed to ignore table');
      setState(prev => ({ ...prev, saving: false }));
    }
  };

  const handleDefineSkipped = async (skippedTable) => {
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      // Remove from backend skipped list
      await unskipTable(skippedTable.server, skippedTable.database, skippedTable.table);
      
      // Update local skipped list
      const newSkippedList = state.skippedTables.filter(
        t => !(t.server === skippedTable.server && t.database === skippedTable.database && t.table === skippedTable.table)
      );
      
      // Fetch fresh data - the unskipped table will now appear as next
      await fetchNextInternal(newSkippedList, state.ignoredTables);
      
      toast.success(`Moved "${skippedTable.table}" back to pending`, { duration: 2000 });
    } catch (error) {
      console.error('Failed to unskip table:', error);
      toast.error('Failed to restore table');
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleRestoreIgnored = async (ignoredTable) => {
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      // Remove from backend ignored list
      await unignoreTable(ignoredTable.server, ignoredTable.database, ignoredTable.table);
      
      // Update local ignored list
      const newIgnoredList = state.ignoredTables.filter(
        t => !(t.server === ignoredTable.server && t.database === ignoredTable.database && t.table === ignoredTable.table)
      );
      
      // Fetch fresh data
      await fetchNextInternal(state.skippedTables, newIgnoredList);
      
      toast.success(`Restored "${ignoredTable.table}" for AI analysis`, { duration: 2000 });
    } catch (error) {
      console.error('Failed to restore table:', error);
      toast.error('Failed to restore table');
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  // Auto-trigger relationship discovery when Phase 3 completes
  const triggerRelationshipDiscovery = async () => {
    setState(prev => ({ ...prev, discoveringRelationships: true }));
    toast.info('🔍 Auto-discovering table relationships...', { duration: 5000 });
    
    try {
      console.log('[Phase 3] Triggering relationship discovery...');
      const result = await discoverRelationships();
      console.log('[Phase 3] Discovery result:', result);
      
      // API returns { suggestions: [...], count: N } not { success, relationships }
      if (result.suggestions && result.suggestions.length > 0) {
        toast.success(`✅ Found ${result.suggestions.length} potential relationships! Check Phase 4 to review.`, { duration: 5000 });
        setState(prev => ({ ...prev, discoveringRelationships: false, relationshipsDiscovered: true }));
        // Notify parent to refresh
        if (typeof onRelationshipsDiscovered === 'function') {
          onRelationshipsDiscovered();
        }
      } else if (result.error) {
        toast.error(`Discovery failed: ${result.error}`, { duration: 5000 });
        setState(prev => ({ ...prev, discoveringRelationships: false }));
      } else {
        toast.info('No new relationships discovered. All key columns may already be mapped in Phase 4.', { duration: 5000 });
        setState(prev => ({ ...prev, discoveringRelationships: false }));
      }
    } catch (error) {
      console.error('[Phase 3] Relationship discovery error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Relationship discovery failed: ${errorMsg}. You can try manually in Phase 4.`);
      setState(prev => ({ ...prev, discoveringRelationships: false }));
    }
  };

  if (state.loading) {
    return (
      <Card className="bg-white border-gray-200">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Table className="w-5 h-5 text-amber-600" />
          Phase 3: Table Definitions
          <span className="ml-2 text-sm font-normal text-gray-400">
            {state.progress.percentage}% complete
          </span>
        </CardTitle>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchNext}
          disabled={state.loading}
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-500">
          Define what each table is used for. The AI will use this to understand your data structure.
          <span className="text-purple-600 ml-1">Use the "Table Review" tab to manage existing definitions.</span>
        </p>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>
              {state.progress.defined} of {state.progress.total} tables defined
              {(state.progress.skipped > 0 || state.skippedTables?.length > 0) && (
                <span className="text-amber-600 ml-2">({state.progress.skipped || state.skippedTables.length} skipped)</span>
              )}
            </span>
            <span className="font-medium">{state.progress.percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-amber-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${state.progress.percentage}%` }}
            />
          </div>
        </div>

        {state.allDone ? (
          <div className="text-center p-8 bg-green-50 rounded-lg">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-green-700 font-medium">All tables have definitions!</p>
            <p className="text-sm text-green-600 mt-1">Phase 3 complete.</p>
            
            {state.discoveringRelationships ? (
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-center gap-2 text-amber-700">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Discovering table relationships...</span>
                </div>
              </div>
            ) : state.relationshipsDiscovered ? (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  ✨ Relationships auto-discovered! Check Phase 4 to review.
                </p>
              </div>
            ) : state.skippedTables?.length > 0 ? (
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700">
                  ⚠️ Define skipped tables first for accurate relationship discovery, or trigger manually below.
                </p>
                <Button 
                  onClick={triggerRelationshipDiscovery}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  disabled={state.discoveringRelationships}
                >
                  <Play className="w-3 h-3 mr-1" />
                  Discover Relationships Anyway
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button 
                  onClick={triggerRelationshipDiscovery}
                  className="bg-blue-500 hover:bg-blue-600"
                  disabled={state.discoveringRelationships}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Discover Relationships
                </Button>
                <p className="text-sm text-gray-500 mt-2">Click to auto-discover table relationships</p>
              </div>
            )}
            
            {/* Skipped Tables Section */}
            {state.skippedTables?.length > 0 && (
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200 text-left">
                <p className="text-sm font-medium text-amber-800 mb-2">
                  ⏭️ Skipped Tables ({state.skippedTables.length})
                </p>
                <p className="text-xs text-amber-600 mb-3">Define these for more accurate relationship discovery</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {state.skippedTables.map((t, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-amber-100">
                      <span className="text-sm text-gray-700">
                        {t.server} / {t.database} / <strong>{t.table}</strong>
                      </span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDefineSkipped(t)}
                        className="text-xs h-7"
                      >
                        Define Now
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ignored Tables Section */}
            {state.ignoredTables?.length > 0 && (
              <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200 text-left">
                <p className="text-sm font-medium text-red-800 mb-2 flex items-center gap-2">
                  <EyeOff className="w-4 h-4" />
                  Ignored Tables - Excluded from AI ({state.ignoredTables.length})
                </p>
                <p className="text-xs text-red-600 mb-3">These tables will NOT be queried by the AI</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {state.ignoredTables.map((t, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-red-100">
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-700 block truncate">
                          {t.server} / {t.database} / <strong>{t.table}</strong>
                        </span>
                        {t.reason && (
                          <span className="text-xs text-gray-500 italic block truncate">
                            {t.reason}
                          </span>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleRestoreIgnored(t)}
                        className="text-xs h-7 ml-2 flex-shrink-0 border-green-200 text-green-600 hover:bg-green-50"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Restore
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : state.currentTable ? (
          <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
            {/* Table Info */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Server className="w-4 h-4" />
                <span>{state.currentTable.server}</span>
                <ChevronRight className="w-3 h-3" />
                <Database className="w-4 h-4" />
                <span>{state.currentTable.database}</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Table className="w-5 h-5 text-amber-600" />
                {state.currentTable.table}
                <span className="text-sm font-normal text-gray-400">
                  ({state.currentTable.row_count?.toLocaleString() || 0} rows)
                </span>
              </h3>
            </div>

            {/* Columns Preview */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Columns in this table:</Label>
              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                {state.currentTable.columns?.slice(0, 20).map((col, idx) => (
                  <span 
                    key={idx} 
                    className="px-2 py-0.5 bg-white border rounded text-xs text-gray-600"
                    title={col.type}
                  >
                    {col.name} <span className="text-gray-400">({col.type?.split('(')[0]})</span>
                  </span>
                ))}
                {state.currentTable.columns?.length > 20 && (
                  <span className="px-2 py-0.5 text-xs text-gray-400">
                    +{state.currentTable.columns.length - 20} more
                  </span>
                )}
              </div>
            </div>

            {/* AI Suggest Button */}
            {isAdmin && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleAISuggest}
                disabled={state.aiSuggesting}
                className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 text-purple-700 hover:from-purple-100 hover:to-indigo-100"
              >
                {state.aiSuggesting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Wand2 className="w-4 h-4 mr-2" />
                )}
                AI Suggest Definition
              </Button>
            )}

            {/* Definition Input */}
            <div className="space-y-2">
              <Label>What is this table used for?</Label>
              <Textarea
                value={state.definition}
                onChange={(e) => setState(prev => ({ ...prev, definition: e.target.value }))}
                placeholder="e.g., This table stores user account information including registration date, email, status, and balance. It's the main user record table."
                rows={3}
                className="bg-white"
              />
            </div>

            {/* Actions */}
            {isAdmin && (
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={handleSave}
                  disabled={state.saving || !state.definition.trim()}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  {state.saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Save & Next
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleSkip}
                  disabled={state.saving}
                  title="Skip for now - you can define it later"
                >
                  <SkipForward className="w-4 h-4 mr-2" />
                  Skip for Later
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setState(prev => ({ ...prev, showIgnoreDialog: true }))}
                  disabled={state.saving}
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  title="Mark as not needed - AI will never query this table"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Ignore - Not Needed
                </Button>
                <span className="text-sm text-gray-400 ml-auto self-center">
                  {state.remaining} remaining
                  {(state.progress.skipped > 0 || state.skippedTables?.length > 0) && (
                    <span className="text-amber-500 ml-1">• {state.progress.skipped || state.skippedTables.length} skipped</span>
                  )}
                  {(state.progress.ignored > 0 || state.ignoredTables?.length > 0) && (
                    <span className="text-red-500 ml-1">• {state.progress.ignored || state.ignoredTables.length} ignored</span>
                  )}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-6">
            <Button onClick={fetchNext}>
              <Play className="w-4 h-4 mr-2" /> Start Defining Tables
            </Button>
          </div>
        )}

        {/* Ignore Table Dialog */}
        <Dialog open={state.showIgnoreDialog} onOpenChange={(open) => setState(prev => ({ ...prev, showIgnoreDialog: open }))}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Ban className="w-5 h-5" />
                Ignore Table - Not Needed for AI
              </DialogTitle>
              <DialogDescription>
                This will permanently exclude <strong>{state.currentTable?.table}</strong> from AI analysis. 
                The AI will never query this table.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  value={state.ignoreReason}
                  onChange={(e) => setState(prev => ({ ...prev, ignoreReason: e.target.value }))}
                  placeholder="e.g., Legacy table, not in use, test data only..."
                />
              </div>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  You can restore this table later from the "Ignored Tables" section if needed.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setState(prev => ({ ...prev, showIgnoreDialog: false }))}>
                Cancel
              </Button>
              <Button 
                onClick={handleIgnore}
                className="bg-red-500 hover:bg-red-600"
              >
                <Ban className="w-4 h-4 mr-2" />
                Ignore Table
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default Phase3TableDefinitions;
